import * as yup from "yup";
import { PropertyKind } from "./types";

export const createPropertyValidationSchema = ({
  hasPropertyOwner = false,
  isBoomScreen = true
}: {
  hasPropertyOwner: boolean;
  isBoomScreen?: boolean;
}) => {
  return yup.object().shape({
    name: yup.string().required("Property name is required"),
    kind: yup
      .string()
      .nullable()
      .oneOf(Object.values(PropertyKind), "Invalid property kind"),
    description: yup.string().optional(),
    external_id: yup.string().optional(),
    owner_name: hasPropertyOwner
      ? yup.string().required("Owner name is required")
      : yup.string().optional(),
    address1: yup.string().required("Street address is required"),
    address2: yup.string().optional(),
    city: yup.string().required("City is required"),
    state_or_province: yup.string().required("State is required"),
    postal_code: yup
      .string()
      .required("Zip is required")
      .min(5, "Zip code must be at least 5 characters")
      .max(10, "Zip code must be less than or equal to 10 characters"),
    country: yup.string().optional(),
    lat: yup.number().optional(),
    lng: yup.number().optional(),

    use_property_group_verification_template: yup.boolean().optional(),
    verification_template_id: yup
      .string()
      .when("use_property_group_verification_template", {
        is: (use_property_group_verification_template: boolean | null) =>
          use_property_group_verification_template !== true && isBoomScreen,
        then: (schema) => schema.required("Verification template is required"),
        otherwise: (schema) => schema.optional()
      }),

    monthly_rent_amount: yup
      .number()
      .typeError("Monthly rent must be a valid number")
      .min(1, "Monthly rent must be at least $1")
      .max(50000, "Monthly rent must be $50,000 or less")
      .test(
        "sf-monthly-rent-check",
        "Monthly rent amount is required",
        function (value) {
          const { kind, home_rent_amount, site_rent_amount } = this.parent;
          if (kind !== PropertyKind.SingleFamily) return true;
          return (
            value != null ||
            home_rent_amount != null ||
            site_rent_amount != null
          );
        }
      ),

    home_rent_amount: yup
      .number()
      .typeError("Home rent must be a valid number")
      .min(0, "Home rent must be at least $0")
      .max(50000, "Home rent must be $50,000 or less")
      .test(
        "sf-home-rent-check",
        "Home rent amount is required",
        function (value) {
          const { kind, monthly_rent_amount } = this.parent;
          if (kind !== PropertyKind.SingleFamily) return true;
          return value != null || monthly_rent_amount != null;
        }
      ),

    site_rent_amount: yup
      .number()
      .typeError("Site rent must be a valid number")
      .min(0, "Site rent must be at least $0")
      .max(50000, "Site rent must be $50,000 or less")
      .test(
        "sf-site-rent-check",
        "Site rent amount is required",
        function (value) {
          const { kind, monthly_rent_amount } = this.parent;
          if (kind !== PropertyKind.SingleFamily) return true;
          return value != null || monthly_rent_amount != null;
        }
      ),

    available_at: yup.string().when("kind", {
      is: PropertyKind.SingleFamily,
      then: (schema) => schema.required("Available at is required"),
      otherwise: (schema) => schema.optional()
    }),

    sqft: yup
      .number()
      .min(0, "Size must be greater than or equal to 0")
      .max(10000, "Size must be less than or equal to 10,000 sq.ft")
      .optional(),
    beds: yup
      .number()
      .integer("Number of beds must be a whole number")
      .min(0, "Number of beds must be greater than or equal to 0")
      .max(10, "Number of beds must be less than or equal to 10")
      .optional(),
    baths: yup
      .number()
      .min(0, "Number of baths must be greater than or equal to 0")
      .max(10, "Number of baths must be less than or equal to 10")
      .optional(),

    amenities: yup
      .array()
      .of(yup.string())
      .when("kind", {
        is: (kind: PropertyKind | null) => kind !== null,
        then: (schema) => schema.default([]),
        otherwise: (schema) => schema.optional()
      }),
    cats_allowed: yup.boolean().when("kind", {
      is: (kind: PropertyKind | null) => kind !== null,
      then: (schema) => schema.default(false),
      otherwise: (schema) => schema.optional()
    }),
    dogs_allowed: yup.boolean().when("kind", {
      is: (kind: PropertyKind | null) => kind !== null,
      then: (schema) => schema.default(false),
      otherwise: (schema) => schema.optional()
    }),

    logo: yup.string().optional(),
    primary_color: yup.string().optional(),
    palette: yup.object().optional(),
    landlord_name: yup
      .string()
      .max(100, "Must be at most 100 characters")
      .optional(),
    landlord_phone: yup
      .string()
      .test(
        "phone-length",
        "Phone number must be exactly 10 characters",
        (value) => !value || value.length === 10
      )
      .optional(),

    units: yup.array().when("kind", {
      is: null,
      then: (schema) =>
        schema
          .of(
            yup.object().shape({
              title: yup.string().required("Unit title is required"),
              monthly_rent_amount: yup
                .number()
                .min(1, "Monthly rent must be at least $1")
                .nullable()
                .test(
                  "sf-monthly-rent-check",
                  "Monthly rent amount is required",
                  function (value) {
                    const { home_rent_amount, site_rent_amount } = this.parent;
                    return (
                      value != null ||
                      home_rent_amount != null ||
                      site_rent_amount != null
                    );
                  }
                ),
              home_rent_amount: yup
                .number()
                .min(0)
                .nullable()
                .test(
                  "sf-home-rent-check",
                  "Home rent amount is required",
                  function (value) {
                    const { monthly_rent_amount } = this.parent;
                    return value != null || monthly_rent_amount != null;
                  }
                ),
              site_rent_amount: yup
                .number()
                .min(0)
                .nullable()
                .test(
                  "sf-site-rent-check",
                  "Site rent amount is required",
                  function (value) {
                    const { monthly_rent_amount } = this.parent;
                    return value != null || monthly_rent_amount != null;
                  }
                ),
              available_at: yup.string().optional(),
              occupied: yup.boolean().optional()
            })
          )
          .required("Units are required when property kind is not set"),
      otherwise: (schema) => schema.optional()
    })
  });
};
